import { useState } from 'react';
import { useMutation } from '@apollo/client/react';

import textConstants from '@atom/constants/textConstants';
import { WORK_ORDER_UPDATE } from '@atom/graph/work';
import { Snackbar } from '@atom/mui';
import { GeoJSON } from '@atom/types/geojson';
import api from '@atom/utilities/api';
import { INVENTORY_ASSETS_ENDPOINT } from '@atom/utilities/endpoints';

type WorkOrderLocationSync = {
  updateWorkLocation: (newLocation: GeoJSON) => Promise<void>;
  updateAssetLocation: (newLocation: GeoJSON) => Promise<void>;
  loading: boolean;
};

const useWorkOrderLocationSync = (
  workOrderId: string,
  inventoryAssetId: string,
  refetch: () => void,
): WorkOrderLocationSync => {
  const [updateWorkDetail, { loading: workUpdateLoading }] =
    useMutation(WORK_ORDER_UPDATE);
  const [assetUpdateLoading, setAssetUpdateLoading] = useState(false);

  const updateWorkLocation = async (newLocation: GeoJSON) => {
    try {
      await updateWorkDetail({
        variables: { workOrder: { id: workOrderId, location: newLocation } },
      });

      refetch();
    } catch {
      Snackbar.error({
        message: textConstants.GENERIC_APPLICATION_ERROR_TEXT,
      });
    }
  };

  const updateAssetLocation = async (newLocation: GeoJSON) => {
    setAssetUpdateLoading(true);

    try {
      await api.patch(`${INVENTORY_ASSETS_ENDPOINT}/${inventoryAssetId}`, {
        location: newLocation,
      });

      refetch();
    } catch {
      Snackbar.error({
        message: textConstants.GENERIC_APPLICATION_ERROR_TEXT,
      });
    } finally {
      setAssetUpdateLoading(false);
    }
  };

  return {
    updateWorkLocation,
    updateAssetLocation,
    loading: workUpdateLoading || assetUpdateLoading,
  };
};

export default useWorkOrderLocationSync;
